import React, { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Box,
  VStack,
  Text,
  Flex,
  Icon,
  Link,
} from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import { FeatureId, UPSELL_FEATURES } from 'src/constants/tier-features';

interface UpsellTierModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureId: FeatureId;
}

const UpsellTierModal = ({ isOpen, onClose, featureId }: UpsellTierModalProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  const feature = UPSELL_FEATURES[featureId];

  if (!feature) {
    console.error(`Feature ${featureId} not found in UPSELL_FEATURES`);
    return null;
  }

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} size="md">
      <AlertDialogOverlay bg="blackAlpha.600" backdropFilter="blur(10px)">
        <AlertDialogContent
          borderRadius="2xl"
          bg="white"
          boxShadow="2xl"
          border="1px solid"
          borderColor="gray.100"
          overflow="hidden"
        >
          {/* Header with gradient background */}
          <Box
            bgGradient="linear(135deg, lomaGreen.400, lomaGreen.800)"
            px={6}
            py={8}
            textAlign="center"
          >
            <VStack spacing={3}>
              <Box
                bg="whiteAlpha.200"
                borderRadius="full"
                p={4}
                backdropFilter="blur(10px)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={feature.icon || ArrowUpIcon} boxSize={8} color="white" />
              </Box>
              <AlertDialogHeader
                fontSize="2xl"
                fontWeight="bold"
                color="white"
                p={0}
                textAlign="center"
              >
                {feature.displayName}
              </AlertDialogHeader>
            </VStack>
          </Box>

          <AlertDialogBody px={8} py={6}>
            <VStack spacing={4} textAlign="center">
              <Text fontSize="md" color="gray.600" lineHeight="1.6">
                {feature.description}
              </Text>

              {/* Tier requirement notice */}
              <Box
                bg="orange.50"
                borderRadius="lg"
                p={3}
                border="1px solid"
                borderColor="orange.100"
                w="full"
              >
                <Text fontSize="sm" color="orange.700" fontWeight="medium">
                  🔒 This feature requires a higher tier subscription
                </Text>
              </Box>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter px={8} pb={6} pt={2}>
            <Flex gap={3} w="full">
              <Button
                ref={cancelRef}
                onClick={onClose}
                variant="outline"
                colorScheme="red"
                flex={1}
                borderRadius="lg"
                h={12}
              >
                Maybe Later
              </Button>
              <Button
                as={Link}
                colorScheme="blue"
                href={`mailto:contact@withloma.com?subject=Upgrade for ${encodeURIComponent(
                  feature.displayName,
                )}`}
                flex={1}
                rel="noopener noreferrer"
                target="_blank"
                bgGradient="linear(135deg, lomaGreen.400, lomaGreen.800)"
                _hover={{
                  bgGradient: 'linear(135deg, lomaGreen.500, lomaGreen.900)',
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg',
                  textDecoration: 'none',
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
                borderRadius="lg"
                h={12}
                fontWeight="semibold"
              >
                Upgrade
              </Button>
            </Flex>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default UpsellTierModal;
